/**
 * Reglas de persistencia del store.
 *
 * Solo sobrevive a la sesión lo que dio el cliente: dirección, complemento,
 * coordenadas y la zona que eligió. Todo lo que el servidor deduce a partir de
 * esas coordenadas —sucursal, polígono y precio de domicilio— se resuelve en
 * vivo cada vez que se entra al flujo de domicilios.
 *
 * Guardarlo era el origen del bug de sucursal equivocada: la dirección seguía
 * apuntando a un polígono resuelto meses atrás, que podía ya no corresponder a
 * la sucursal en la que el cliente terminaba comprando.
 */
export const PERSISTED_MODULES = [
  'template', 'profile', 'customer', 'order', 'shared', 'ontable', 'cluvipay', 'coupons', 'delivery'
];

// Campos de profile.location que deduce el servidor a partir de las coordenadas.
const DERIVED_LOCATION_KEYS = ['supplier_id', 'polygon_id', 'location_price'];

// Campos de customer que deduce el servidor. `supplier` se re-resuelve por el
// slug de la URL en cada arranque (App.validateNeedSupplier y las vistas con
// slug); persistirlo hacía que una pestaña nueva heredara la sede de otra y
// pidiera content_home/content_popups con ese id antes de que GetSupplier
// respondiera con la sede de la URL.
const DERIVED_CUSTOMER_KEYS = ['polygon', 'supplier'];

const omit = (obj, keys) => {
  const next = { ...obj };
  keys.forEach(key => delete next[key]);
  return next;
};

/**
 * Quita del estado los datos derivados.
 *
 * Se aplica al guardar y también al rehidratar. Lo segundo es lo que limpia los
 * navegadores que ya los tienen guardados de versiones anteriores: no hace
 * falta migración ni cambiar la key de localStorage, basta con desplegar.
 */
export const stripDerivedState = (state) => {
  if (!state || typeof state !== 'object') return state;
  const next = { ...state };

  if (next.profile?.location) {
    next.profile = { ...next.profile, location: omit(next.profile.location, DERIVED_LOCATION_KEYS) };
  };
  if (next.customer) {
    next.customer = omit(next.customer, DERIVED_CUSTOMER_KEYS);
  };

  return next;
};

// Reducer de vuex-persist: elige los módulos que se guardan y les quita lo derivado.
export const persistedState = (state) => stripDerivedState(
  PERSISTED_MODULES.reduce((acc, name) => {
    if (state[name] !== undefined) acc[name] = state[name];
    return acc;
  }, {})
);

// restoreState de vuex-persist: poda al hidratar y tolera un localStorage corrupto.
export const restorePersistedState = (key, storage) => {
  try {
    const raw = storage.getItem(key);
    const saved = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
    return stripDerivedState(saved);
  } catch (error) {
    console.error(error);
    return {};
  }
};
